import { Component, OnInit, Input } from "@angular/core";
import { DashboardService } from "../dashboard.service";
import  moment from "moment";
import { OpensAndViews, TopSenders } from "../dashboard.model";
import { CorpClient } from "src/app/shared-models/client.model";
import { CorpClientsService } from "src/app/corp-clients.service";
import Auth, { CognitoUser } from "@aws-amplify/auth";

@Component({
    selector: "dashboard-stats",
    templateUrl: "./stats.component.html",
    styleUrls: ["./stats.component.css"],
    standalone: false
})
export class StatsComponent implements OnInit {
  _opensAndViewsData: OpensAndViews[];
  isLoadingTotals = false;
  error: string;
  totalSalesMails = 0;
  totalOpens = 0;
  totalViews = 0;
  mostViewedTime = "";
  mostViewedDay = "";
  mostOpenedTime = "";
  mostOpenedDay = "";
  currentClient: CorpClient;
  openHoursWithMaxValue = [];

  @Input("opensAndViewsData")
  set opensAndViewsData(value: OpensAndViews[]) {
    this._opensAndViewsData = value;
    if (value) {
      this.calcStats();
    }
  }
  get opensAndViewsData(): OpensAndViews[] {
    return this._opensAndViewsData;
  }

  private _days: number = 90;
  @Input("days")
  set days(value: number) {
    this._days = value;
    this.getSmTotals();
  }
  get days(): number {
    return this._days;
  }

  @Input("isLoading") isLoading: boolean = false;

  constructor(
    private api: DashboardService,
    private corpService: CorpClientsService
  ) {
    this.corpService.currentClient.subscribe((result) => {
      if (result == null) {
        return;
      }
      this.currentClient = result;
      this.getSmTotals();
    });
  }

  ngOnInit() {}

  async getSmTotals() {
    if (this.currentClient != null) {
      this.error = null;
      this.isLoadingTotals = true;
      this.totalSalesMails = 0;
      this.api
        .getSalesMailsTotals(this.days, this.currentClient.client_id)
        .then((response) => {
          this.totalSalesMails = response.value[0].total_salesmails;
        })
        .catch((error) => {
          console.log(error);
          this.error = "Could not load chart data at this time.";
        })
        .finally(() => {
          this.isLoadingTotals = false;
        });
    } else {
      this.error = null;
      this.isLoadingTotals = true;
      this.totalSalesMails = 0;

      let cognitoUser: CognitoUser = await Auth.currentAuthenticatedUser();
      cognitoUser.getUserAttributes((err, result) => {
        let clientId = result
          .filter((item) => item.getName() == "custom:client_id")
          .map((att) => att.getValue())[0]
          .toString();

        this.api
          .getSalesMailsTotalsStd(this.days, +clientId)
          .then((response) => {
            this.totalSalesMails = response.value[0].total_salesmails;
          })
          .catch((error) => {
            console.log(error);
            this.error = "Could not load chart data at this time.";
          })
          .finally(() => {
            this.isLoadingTotals = false;
          });
      });
    }
  }

  calcStats() {
    if (!this._opensAndViewsData || this._opensAndViewsData.length == 0) {
      console.debug("no data, exiting");
      return;
    }

    this.totalOpens = 0;
    this.totalViews = 0;
    this.mostViewedTime = "";
    this.mostViewedDay = "";
    this.mostOpenedTime = "";
    this.mostOpenedDay = "";
    this.openHoursWithMaxValue = [];

    var o = this._opensAndViewsData.filter((item) => item.type == "Open");
    var v = this._opensAndViewsData.filter((item) => item.type == "View");

    this.totalOpens = o.length;
    this.totalViews = v.length;

    //Find most opened time
    var possibleOpenHours = new Array(24).fill(0);
    o.forEach((item) => {
      var hour = moment(item.cre_date).hour();
      possibleOpenHours[hour] += 1;
    });
    var maxValue = Math.max(...possibleOpenHours);
    var iterator = possibleOpenHours.keys();

    for (let key of iterator) {
      if (possibleOpenHours[key] == maxValue) {
        this.openHoursWithMaxValue.push(key);
      }
    }

    this.openHoursWithMaxValue.forEach((hour) => {
      if (this.mostOpenedTime.length > 0) {
        this.mostOpenedTime += ", ";
      }
      this.mostOpenedTime += this.getHourString(hour);
    });

    //Find most viewed time
    var possibleViewHours = new Array(24).fill(0);
    v.forEach((item) => {
      var hour = moment(item.cre_date).hour();
      possibleViewHours[hour] += 1;
    });
    var maxValue = Math.max(...possibleViewHours);
    var ViewHoursWithMaxValue = [];
    var iterator = possibleViewHours.keys();
    for (let key of iterator) {
      if (possibleViewHours[key] == maxValue) {
        ViewHoursWithMaxValue.push(key);
      }
    }

    ViewHoursWithMaxValue.forEach((hour) => {
      if (this.mostViewedTime.length > 0) {
        this.mostViewedTime += ", ";
      }
      this.mostViewedTime += this.getHourString(hour);
    });

    //Get most opened days
    var possibleOpenDays = Array(7).fill(0);
    o.forEach((item) => {
      var day = moment(item.cre_date).day();
      possibleOpenDays[day] += 1;
    });
    var maxOpenDayValue = Math.max(...possibleOpenDays);
    var openDaysWithMaxValues = [];
    var iterator = possibleOpenDays.keys();
    for (let key of iterator) {
      if (possibleOpenDays[key] == maxOpenDayValue) {
        openDaysWithMaxValues.push(key);
      }
    }

    openDaysWithMaxValues.forEach((day) => {
      if (this.mostOpenedDay.length > 0) {
        this.mostOpenedDay += ", ";
      }
      this.mostOpenedDay += this.getDayString(day);
    });

    //Get most viewed days
    var possibleViewDays = Array(7).fill(0);
    v.forEach((item) => {
      var day = moment(item.cre_date).day();
      possibleViewDays[day] += 1;
    });
    var maxViewDayValue = Math.max(...possibleViewDays);
    var ViewDaysWithMaxValues = [];
    var iterator = possibleViewDays.keys();
    for (let key of iterator) {
      if (possibleViewDays[key] == maxViewDayValue) {
        ViewDaysWithMaxValues.push(key);
      }
    }
    ViewDaysWithMaxValues.forEach((day) => {
      if (this.mostViewedDay.length > 0) {
        this.mostViewedDay += ", ";
      }
      this.mostViewedDay += this.getDayString(day);
    });
  }

  getHourString(hour: number) {
    if (hour == 0) {
      return "12 AM";
    } else if (hour == 12) {
      return "12 PM";
    } else if (hour > 12) {
      hour = hour - 12;
      return hour + " PM";
    } else {
      return hour + " AM";
    }
  }

  getDayString(day: number) {
    switch (day) {
      case 0:
        return "Sunday";
      case 1:
        return "Monday";
      case 2:
        return "Tuesday";
      case 3:
        return "Wednesday";
      case 4:
        return "Thursday";
      case 5:
        return "Friday";
      case 6:
        return "Saturday";
      default:
        return "";
    }
  }
}
